import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { resolveMediaUrl } from "../lib/media";
import NotificationBell from "./NotificationBell";
import {
  House, Users, ClipboardText, ChartLine, Trophy, CalendarBlank,
  Megaphone, Gear, SignOut, SoccerBall, List, X, UserCircle, UserPlus, Cake
} from "@phosphor-icons/react";

const NAV_ITEMS = [
  { to: "/dashboard", label: "Dashboard", icon: House },
  { to: "/players", label: "Players", icon: Users },
  { to: "/birthdays", label: "Birthdays", icon: Cake },
  { to: "/pending", label: "Pending", icon: UserPlus, badgeKey: "pending" },
  { to: "/attendance", label: "Attendance", icon: ClipboardText },
  { to: "/matches", label: "Matches", icon: Trophy },
  { to: "/practices", label: "Practices", icon: SoccerBall },
  { to: "/schedule", label: "Schedule", icon: CalendarBlank },
  { to: "/announcements", label: "Announcements", icon: Megaphone },
  { to: "/settings", label: "Settings", icon: Gear },
];

const MOBILE_TABS = NAV_ITEMS.slice(0, 4);

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState({ academy_name: "Wahdat FC Academy", academy_logo_url: "" });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    const load = () => api.get("/settings").then((r) => setSettings(r.data || {})).catch(() => {});
    load();
    const onUpdate = () => load();
    window.addEventListener("wahdat:settings-updated", onUpdate);
    return () => window.removeEventListener("wahdat:settings-updated", onUpdate);
  }, []);

  useEffect(() => {
    const loadPending = () => api.get("/register/pending/count").then((r) => setPendingCount(r.data?.count || 0)).catch(() => {});
    loadPending();
    const t = setInterval(loadPending, 60000);
    const onUpdate = () => loadPending();
    window.addEventListener("wahdat:pending-updated", onUpdate);
    return () => { clearInterval(t); window.removeEventListener("wahdat:pending-updated", onUpdate); };
  }, []);

  function handleLogout() {
    logout();
    navigate("/login");
  }

  return (
    <div className="min-h-screen flex bg-[#050505] text-[#F5F5F5]">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 flex-col border-r border-neutral-900 bg-[#0a0a0a] sticky top-0 h-screen">
        <div className="p-6 border-b border-neutral-900 flex items-center gap-3">
          {settings.academy_logo_url ? (
            <img src={resolveMediaUrl(settings.academy_logo_url)} alt="logo" className="w-11 h-11 object-contain" />
          ) : (
            <div className="w-11 h-11 rounded-md bg-[#D90429] flex items-center justify-center">
              <SoccerBall size={24} weight="fill" color="#fff" />
            </div>
          )}
          <div>
            <div className="font-display font-bold text-lg leading-none uppercase tracking-tight">Wahdat FC</div>
            <div className="overline mt-1">Academy</div>
          </div>
        </div>
        <nav className="flex-1 p-3 overflow-y-auto">
          {NAV_ITEMS.map((it) => {
            if (it.to === "/settings" && user?.role !== "admin") return null;
            const badge = it.badgeKey === "pending" ? pendingCount : 0;
            return (
              <NavLink
                key={it.to}
                to={it.to}
                data-testid={`nav-${it.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-md text-sm font-semibold my-0.5 transition-colors ${
                    isActive
                      ? "bg-[#D90429] text-white"
                      : "text-neutral-400 hover:text-white hover:bg-neutral-900"
                  }`
                }
              >
                <it.icon size={20} weight="bold" />
                <span className="flex-1">{it.label}</span>
                {badge > 0 && (
                  <span data-testid={`nav-badge-${it.label.toLowerCase()}`} className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#FFD166] text-[#050505] text-[10px] font-black">
                    {badge}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-3 border-t border-neutral-900">
          <div className="mb-2">
            <NotificationBell variant="sidebar" />
          </div>
          <NavLink
            to="/profile"
            data-testid="nav-profile"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${
                isActive ? "bg-neutral-900" : "hover:bg-neutral-900"
              }`
            }
          >
            <UserCircle size={32} weight="fill" className="text-neutral-500" />
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate">{user?.name}</div>
              <div className="overline">{user?.role}</div>
            </div>
          </NavLink>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full mt-2 flex items-center gap-2 px-4 py-3 rounded-md text-sm font-semibold text-neutral-400 hover:text-white hover:bg-neutral-900 transition-colors"
          >
            <SignOut size={20} weight="bold" /> Log Out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="lg:hidden sticky top-0 z-30 bg-[#050505]/95 backdrop-blur border-b border-neutral-900 px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {settings.academy_logo_url ? (
              <img src={resolveMediaUrl(settings.academy_logo_url)} alt="logo" className="w-9 h-9 object-contain" />
            ) : (
              <div className="w-9 h-9 rounded-md bg-[#D90429] flex items-center justify-center">
                <SoccerBall size={18} weight="fill" color="#fff" />
              </div>
            )}
            <div className="font-display font-bold text-base uppercase tracking-tight">Wahdat FC</div>
          </div>
          <div className="flex items-center gap-1">
            <NotificationBell variant="topbar" />
            <button
              onClick={() => setDrawerOpen(true)}
              data-testid="mobile-menu-btn"
              className="p-2 rounded-md hover:bg-neutral-900"
            >
              <List size={24} weight="bold" />
            </button>
          </div>
        </header>

        {drawerOpen && (
          <div className="lg:hidden fixed inset-0 z-40 bg-black/70" onClick={() => setDrawerOpen(false)}>
            <div className="absolute right-0 top-0 h-full w-72 bg-[#0a0a0a] border-l border-neutral-900 p-4" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-4">
                <div className="overline">Menu</div>
                <button onClick={() => setDrawerOpen(false)} className="p-2 rounded-md hover:bg-neutral-900" data-testid="mobile-menu-close">
                  <X size={20} weight="bold" />
                </button>
              </div>
              {NAV_ITEMS.map((it) => {
                if (it.to === "/settings" && user?.role !== "admin") return null;
                const badge = it.badgeKey === "pending" ? pendingCount : 0;
                return (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    onClick={() => setDrawerOpen(false)}
                    data-testid={`mobile-nav-${it.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-4 py-3 rounded-md text-sm font-semibold my-0.5 transition-colors ${
                        isActive ? "bg-[#D90429] text-white" : "text-neutral-400 hover:text-white hover:bg-neutral-900"
                      }`
                    }
                  >
                    <it.icon size={20} weight="bold" />
                    <span className="flex-1">{it.label}</span>
                    {badge > 0 && (
                      <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#FFD166] text-[#050505] text-[10px] font-black">{badge}</span>
                    )}
                  </NavLink>
                );
              })}
              <div className="border-t border-neutral-900 mt-3 pt-3">
                <NavLink
                  to="/profile"
                  onClick={() => setDrawerOpen(false)}
                  data-testid="mobile-nav-profile"
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-md text-sm font-semibold my-0.5 ${
                      isActive ? "bg-neutral-900" : "hover:bg-neutral-900"
                    }`
                  }
                >
                  <UserCircle size={20} weight="bold" /> Profile
                </NavLink>
                <div className="px-4 py-2">
                  <div className="text-sm font-semibold">{user?.name}</div>
                  <div className="overline">{user?.role}</div>
                </div>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2 px-4 py-3 rounded-md text-sm font-semibold text-neutral-400 hover:text-white hover:bg-neutral-900"
                  data-testid="mobile-logout-btn"
                >
                  <SignOut size={20} weight="bold" /> Log Out
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Main content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-10 pb-24 lg:pb-10">
          <Outlet />
        </main>

        {/* Mobile bottom nav */}
        <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-[#0a0a0a] border-t border-neutral-900 grid grid-cols-4">
          {MOBILE_TABS.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={`bottom-nav-${it.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center py-3 gap-1 text-[10px] font-bold uppercase tracking-widest ${
                  isActive ? "text-[#D90429]" : "text-neutral-500"
                }`
              }
            >
              <it.icon size={22} weight={"bold"} /> {it.label}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
